import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { GALLERY, GALLERY_CATS, fadeUp } from "../../data";

export const Gallery = () => {
  const [cat, setCat] = useState("All");
  const [active, setActive] = useState(null);
  const items = cat === "All" ? GALLERY : GALLERY.filter((g) => g.cat === cat);

  const nav = (dir) => {
    setActive((prev) => {
      const n = (prev + dir + items.length) % items.length;
      return n;
    });
  };

  return (
    <section id="gallery" className="relative py-24 md:py-32" style={{ background: "#FFFFFF" }}>
      <div className="container-x">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="mb-10">
          <span className="eyebrow" style={{ color: "var(--blue)" }}>Gallery</span>
          <h2 className="font-display font-bold tracking-tight text-3xl md:text-4xl lg:text-5xl mt-4" style={{ color: "var(--navy)" }}>
            A few moments along the way.
          </h2>
        </motion.div>

        <div className="flex flex-wrap gap-2.5 mb-10">
          {GALLERY_CATS.map((c) => (
            <button key={c} onClick={() => setCat(c)} data-testid={`gallery-filter-${c.toLowerCase().replace(/\s/g, "-")}`}
              className={`font-body text-sm font-medium px-4 py-2 rounded-full border transition-all duration-300 ${
                cat === c ? "text-white" : ""}`}
              style={cat === c
                ? { background: "linear-gradient(135deg,#1976D2,#0D47A1)", borderColor: "transparent", boxShadow: "0 6px 16px rgba(25,118,210,0.28)" }
                : { borderColor: "var(--grey)", color: "var(--navy)", background: "#fff" }}>
              {c}
            </button>
          ))}
        </div>

        <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 [column-fill:_balance]">
          {items.map((g, i) => (
            <motion.button key={`${g.src}-${i}`} layout
              initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.5, delay: (i % 3) * 0.06 }}
              onClick={() => setActive(i)} data-testid={`gallery-item-${i}`}
              className="group relative mb-5 w-full overflow-hidden rounded-2xl break-inside-avoid block shadow-[0_8px_30px_rgba(0,0,0,0.06)]">
              <img src={g.src} alt={`Puttraj — ${g.label} (${g.cat})`} loading="lazy" decoding="async"
                className="w-full object-cover group-hover:scale-105 transition-transform duration-[900ms]" />
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-5"
                style={{ background: "linear-gradient(to top, rgba(7,26,61,0.85), transparent 60%)" }}>
                <div>
                  <span className="eyebrow" style={{ color: "var(--gold)" }}>{g.cat}</span>
                  <p className="font-display text-ivory font-medium mt-1">{g.label}</p>
                </div>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active !== null && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            data-testid="gallery-lightbox"
            className="fixed inset-0 z-[70] flex items-center justify-center p-4" style={{ background: "rgba(5,12,24,0.95)" }}
            onClick={() => setActive(null)}>
            <button data-testid="lightbox-close" className="absolute top-5 right-5 p-2" onClick={() => setActive(null)} aria-label="Close">
              <X size={30} color="var(--ivory)" />
            </button>
            <button className="absolute left-4 md:left-10 p-2" onClick={(e) => { e.stopPropagation(); nav(-1); }} aria-label="Previous">
              <ChevronLeft size={36} color="var(--ivory)" />
            </button>
            <motion.figure key={active} initial={{ scale: 0.92, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
              className="max-w-4xl max-h-[85vh]" onClick={(e) => e.stopPropagation()}>
              <img src={items[active].src} alt={items[active].label} className="max-h-[78vh] w-auto mx-auto rounded-xl object-contain" />
              <figcaption className="text-center mt-4">
                <span className="eyebrow" style={{ color: "var(--gold)" }}>{items[active].cat}</span>
                <p className="font-display text-ivory mt-1">{items[active].label}</p>
              </figcaption>
            </motion.figure>
            <button className="absolute right-4 md:right-10 p-2" onClick={(e) => { e.stopPropagation(); nav(1); }} aria-label="Next">
              <ChevronRight size={36} color="var(--ivory)" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
